import axios from 'axios';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { ENV_CONFIG } from '../../shared/config/env.config';
import { toast } from 'react-toastify';

type DynamicBaseQueryArgs = FetchArgs & {
  service?: string;
  /**
   * Drops the `Authorization` / `Token` headers for this request. Needed for
   * pre-signed S3 URLs, which carry their own credentials in the query string
   * and reject a second auth mechanism. Angular's HttpInterceptorService did the
   * same by cloning those requests with an empty header set.
   */
  skipAuthHeaders?: boolean;
};

const SERVICE_URL_MAP: Record<string, string> = {
  api: ENV_CONFIG.apiUrl,
  login: ENV_CONFIG.loginUrl,
  /** Files served by this app itself, e.g. public/assets/json/*.json. */
  static: '',
};

/** Endpoints that already know their full host skip the service base URL. */
const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);

const getApiErrorMessage = (status: number | string, data: any) => {
  const backendMessage =
    data?.message ||
    data?.error ||
    data?.detail ||
    data?.msg ||
    (typeof data === 'string' ? data : '');

  switch (status) {
    case 'FETCH_ERROR':
      return (
        backendMessage ||
        'Unable to connect to server. Please check your network or server status.'
      );

    case 0:
      return 'Unable to connect to server. Please check your internet connection or server status.';

    case 400:
      return (
        backendMessage || 'Invalid request. Please check the submitted data.'
      );

    case 401:
      return backendMessage || 'Your session has expired. Please login again.';

    case 403:
      return (
        backendMessage || 'You do not have permission to perform this action.'
      );

    case 404:
      return backendMessage || 'Requested data was not found.';

    case 408:
      return backendMessage || 'Request timeout. Please try again.';

    case 409:
      return (
        backendMessage ||
        'This is a duplicate entry. Please check and try again'
      );

    case 422:
      return (
        backendMessage || 'Validation failed. Please check the entered details.'
      );

    case 429:
      return (
        backendMessage || 'Too many requests. Please try again after some time.'
      );

    case 500:
      return 'Internal server error occurred.';

    case 502:
      return (
        backendMessage || 'Bad gateway. Server is not responding properly.'
      );

    case 503:
      return (
        backendMessage ||
        'Service temporarily unavailable. Please try again later.'
      );

    case 504:
      return (
        backendMessage || 'Gateway timeout. Server took too long to respond.'
      );

    default:
      return backendMessage || 'Something went wrong. Please try again later.';
  }
};

export const dynamicBaseQuery: BaseQueryFn<
  string | DynamicBaseQueryArgs,
  unknown,
  FetchBaseQueryError
> = async (args, _api, _extraOptions) => {
  let service: string = 'php1';
  let skipAuthHeaders = false;
  let modifiedArgs: FetchArgs;

  if (typeof args === 'string') {
    modifiedArgs = { url: args };
  } else {
    const {
      service: extractedService,
      skipAuthHeaders: extractedSkipAuthHeaders,
      ...restArgs
    } = args;
    service = extractedService ?? 'php1';
    skipAuthHeaders = extractedSkipAuthHeaders ?? false;
    modifiedArgs = restArgs;
  }

  const baseUrl = SERVICE_URL_MAP[service];

  const {
    url,
    method = 'GET',
    body,
    params,
    headers: argHeaders,
  } = modifiedArgs;

  const authKey = localStorage.getItem('AuthorizationKey');
  // const authKey = process.env?.REACT_APP_API_loggedInAuthorization;
  const token = localStorage.getItem('token');
  try {
    const result = await axios({
      url: isAbsoluteUrl(url) ? url : `${baseUrl}/${url}`,
      method,
      data: body,
      params,
      headers: {
        Accept: 'application/json, text/plain, */*',
        ...(!skipAuthHeaders && authKey && { Authorization: authKey }),
        ...(!skipAuthHeaders && token && { Token: token }),
        ...(argHeaders as Record<string, string>),
      },
    });

    return { data: result.data };
  } catch (error: any) {
    const status = error.response?.status ?? 'FETCH_ERROR';
    const responseData = error.response?.data ?? error.message;

    const errorMessage = getApiErrorMessage(status, responseData);
    toast.error(errorMessage);

    console.error('API Error:', {
      status,
      url: error.config?.url,
      method: error.config?.method,
      data: responseData,
      message: errorMessage,
    });

    if (status === 401) {
      localStorage.clear();
      // Notify the app-shell guard (AppLayout) to redirect to login. The
      // native `storage` event does not fire in the tab that made the change,
      // so we dispatch our own event for the same-tab case.
      window.dispatchEvent(new Event('auth:logout'));
    }

    return {
      error: {
        status,
        data: {
          originalError: responseData,
          message: errorMessage,
        },
      } as FetchBaseQueryError,
    };
  }
};
